import { styled, alpha } from '@mui/system';
import { Card } from '@mui/material';
import InputBase from '@mui/material/InputBase';

// Color constants
export const blue = {
  100: '#DAECFF',
  200: '#b6daff',
  400: '#3399FF',
  500: '#007FFF',
  600: '#0072E5',
  900: '#003A75',
};

export const grey = {
  50: '#F3F6F9',
  100: '#E5EAF2',
  200: '#DAE2ED',
  300: '#C7D0DD',
  400: '#B0B8C4',
  500: '#9DA8B7',
  600: '#6B7A90',
  700: '#434D5B',
  800: '#303740',
  900: '#1C2025',
};

// Styled Input Component
export const StyledInput = styled(InputBase)(
  ({ theme }) => `
    width: 100%;
    font-family: 'Outfit', 'Inter', 'Poppins', sans-serif;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.5;
    padding: 8px 12px;
    height: 40px;
    border-radius: 10px;
    color: ${theme.palette.mode === 'dark' ? grey[300] : grey[900]};
    background: ${theme.palette.mode === 'dark' ? alpha(grey[900], 0.4) : alpha('#fff', 0.6)};
    backdrop-filter: blur(8px);
    border: 1px solid ${theme.palette.mode === 'dark' ? alpha(grey[700], 0.5) : alpha(grey[200], 0.5)};
    box-shadow: 0 4px 6px ${alpha('#000', 0.02)};
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    @media (max-width: 600px) {
      max-width: 100%;
    }

    &:hover {
      border-color: #3DAC5A;
    }

    &:focus {
      outline: 0;
      border-color: #3DAC5A;
      background: ${theme.palette.mode === 'dark' ? alpha(grey[900], 0.6) : '#fff'};
      box-shadow: 0 0 0 4px ${alpha('#3DAC5A', 0.15)};
    }
  `
);

// Styled Label Component
export const Label = styled('p')`
  margin: 0 0 6px 4px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.025em;
`;

// Glass Card Component
export const GlassCard = styled(Card)(({ theme }) => ({
  background: theme.palette.mode === 'dark' ? alpha('#1e293b', 0.4) : alpha('#ffffff', 0.5),
  backdropFilter: 'blur(16px)',
  borderRadius: '24px',
  border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.05) : alpha('#ffffff', 0.2)}`,
  boxShadow: `0 8px 32px 0 ${alpha('#000', 0.08)}`,
  overflow: 'visible',
}));

// Shared MUI <Select> MenuProps: makes the currently-selected MenuItem(s)
// unmistakable at a glance (tinted background + trailing checkmark), instead
// of relying on MUI's default Mui-selected shade, which is too close to the
// hover shade to tell apart in a long list. Works for both single- and
// multi-select — MUI applies .Mui-selected to every currently-selected
// MenuItem in both modes. Spread this into any <Select>'s MenuProps prop:
//   <Select MenuProps={SELECTED_MENU_PROPS} ...>
// or, if the Select already sets its own MenuProps, merge them:
//   <Select MenuProps={{ ...SELECTED_MENU_PROPS, ...myMenuProps }} ...>
export const SELECTED_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 340,
      '& .MuiMenuItem-root.Mui-selected': {
        backgroundColor: alpha('#3DAC5A', 0.16),
        fontWeight: 700,
        color: '#1e293b',
        '&:hover': { backgroundColor: alpha('#3DAC5A', 0.24) },
      },
      '& .MuiMenuItem-root.Mui-selected::after': {
        content: '"✓"',
        marginLeft: 'auto',
        paddingLeft: 12,
        color: '#3DAC5A',
        fontWeight: 800,
      },
    },
  },
};